import { memo } from "react";
import clsx from "clsx";
import { Icon } from "@cntrl/ui";
import type { components } from "@cntrl/api-client";
import { useInboxBadge } from "../../app/hooks/useInbox.js";
import { mobileNavIcon } from "../mobile/mobileTabs.js";
import tab from "../mobile/MobileShell.module.scss";

type MobileTab = components["schemas"]["MobileTab"];

export interface RosterMobileTabBarProps {
  /** The org mobile tab set from the server nav document (`nav.mobile.tabs` on the org context). */
  tabs: MobileTab[];
  /** The active tab KEY in the BAR's own vocabulary (mobile "home", not the route's "dashboard") — the shell
   *  maps the route's active tab into this before passing it, so the bar needs no route knowledge. */
  activeKey: string | undefined;
  /** Select a tab by KEY. The shell maps mobile keys to roster routes (home → /roster, inbox → /roster/messages,
   *  scoped → the artist-required gate or the focused artist). `more` is handled by `onOpenMore` instead. */
  onSelect: (key: string) => void;
  /** Open the More sheet (the `more` tab has no route). */
  onOpenMore: () => void;
}

/**
 * The roster's mobile bottom bar (ART-155). It renders the SAME server-granted org tab set the desktop roster
 * strip does, but selection and active-state are resolved by the SHELL (which owns roster routing) rather than
 * the band shell's key→route map — so it reuses the styles of `MobileTabBar` and none of its band routing.
 *
 * `memo`'d like `MobileTabBar` — the shell re-renders on every sheet/dialog toggle, but the bar only needs to
 * repaint when `activeKey` or the tab set changes, so the shell stabilizes its callback props.
 */
export const RosterMobileTabBar = memo(function RosterMobileTabBar({
  tabs,
  activeKey,
  onSelect,
  onOpenMore,
}: RosterMobileTabBarProps) {
  // The Inbox tab counts unread THREADS (useInboxBadge), never the bell's notification count — the same
  // two-badges rule the band bar carries. Only the Management lens ever renders an inbox tab (OrgTabKeys +
  // the hasInbox filter), so this is silent for label/booking (ART-153 pass two: the roster bar was the one
  // inbox with no unread signal).
  const inboxBadge = useInboxBadge();

  return (
    <nav className={tab.tabbar} aria-label="Primary">
      {tabs.map((t) => {
        const key = t.key ?? "";
        const Glyph = mobileNavIcon(key);
        const active = !t.center && key === activeKey;
        const badge = key === "inbox" ? (inboxBadge.data ?? 0) : 0;
        return (
          <button
            key={key}
            type="button"
            className={clsx(tab.tab, t.center && tab.tabCenter, active && tab.tabActive)}
            aria-current={active ? "page" : undefined}
            // The count lives in the BUTTON's accessible name (a badge span that names itself would be
            // announced twice); the badge itself is decorative — the band bar's exact pattern.
            aria-label={badge > 0 ? `${t.label ?? key}, ${badge} unread` : (t.label ?? key)}
            onClick={key === "more" ? onOpenMore : () => onSelect(key)}
          >
            <span className={tab.tabIcon}>
              <Icon icon={Glyph} size={t.center ? 22 : 20} />
              {badge > 0 && (
                <span className={tab.tabBadge} aria-hidden="true">
                  {badge > 99 ? "99+" : badge}
                </span>
              )}
            </span>
            <span className={tab.tabLabel}>{t.label}</span>
          </button>
        );
      })}
    </nav>
  );
});
